import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { CoreData, NameEntry } from "../data/types";
import { saveRaw, useStore } from "../state/store";
import {
  activeFilterCount,
  canUndo,
  decide,
  decisions,
  filters,
  haptic,
  settings,
  ui,
  undo,
  undoKey,
  type UndoStep,
} from "../state/app";
import { bringToFront, deck, deckFocus, otherSpellings, remainingCount, upcoming } from "../state/deck";
import { checkInstantMatch, partner, partnerName, partnerSets } from "../state/partner";
import { goTab, openName, openOverlay, route } from "../state/router";
import { entryFor, prefetchDetails } from "../data/load";
import { tasteAfter, tasteReasons } from "../state/taste";
import { fmt, sexIdxFor } from "../data/format";
import { NameCard } from "./NameCard";
import { bump, cheer, tally } from "./cheer";
import { IconHeart, IconInfo, IconSliders, IconStar, IconTwoHearts, IconUndo, IconX } from "./icons";

type Dir = "left" | "right" | "up";

interface CardHandle {
  fling(dir: Dir): void;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

function SwipeCard({
  entry,
  depth,
  handleRef,
  enterFrom,
  reduceMotion,
  demo,
  onCommit,
  onTap,
  children,
}: {
  entry: NameEntry;
  depth: number;
  handleRef: { current: CardHandle | null };
  enterFrom: Dir | null;
  reduceMotion: boolean;
  /** lean the card right and left once, to show how swiping works */
  demo?: boolean;
  onCommit: (e: NameEntry, dir: Dir) => void;
  onTap: (e: NameEntry) => void;
  children: ComponentChildren;
}) {
  const el = useRef<HTMLDivElement>(null);
  /** which verdict letting go now would give: the stamp snaps up and the phone ticks as it changes */
  const armed = useRef<"" | "like" | "nope" | "love">("");
  const s = useRef({ active: false, id: -1, sx: 0, sy: 0, dx: 0, dy: 0, t0: 0, lx: 0, ly: 0, lt: 0, vx: 0, vy: 0, leaving: false });
  const isTop = depth === 0;

  function deckEl(): HTMLElement | null {
    return el.current?.parentElement ?? null;
  }

  function paint(dx: number, dy: number) {
    const node = el.current;
    if (!node) return;
    node.style.transform = `translate3d(${dx}px, ${dy}px, 0) rotate(${dx * 0.055}deg)`;
    const vertical = -dy > Math.abs(dx);
    node.style.setProperty("--s-like", String(vertical ? 0 : clamp01(dx / 100)));
    node.style.setProperty("--s-nope", String(vertical ? 0 : clamp01(-dx / 100)));
    node.style.setProperty("--s-love", String(vertical ? clamp01(-dy / 110) : 0));
    deckEl()?.style.setProperty("--progress", String(clamp01(Math.hypot(dx, dy) / 140)));
    // the same thresholds as letting go (see up())
    const upward = -dy > Math.abs(dx) * 1.1;
    arm(upward && dy < -120 ? "love" : !upward && dx > 105 ? "like" : !upward && dx < -105 ? "nope" : "");
  }

  function arm(next: "" | "like" | "nope" | "love") {
    if (next === armed.current || !el.current) return;
    armed.current = next;
    el.current.dataset.armed = next;
    if (next) haptic(4);
  }

  function fling(dir: Dir) {
    const st = s.current;
    const node = el.current;
    if (st.leaving || !node) return;
    st.leaving = true;
    deckEl()?.classList.remove("dragging");
    const w = window.innerWidth;
    const h = window.innerHeight;
    const tx = dir === "left" ? -(w + 160) : dir === "right" ? w + 160 : st.dx * 0.4;
    const ty = dir === "up" ? -(h + 160) : st.dy + st.vy * 160;
    const rot = dir === "up" ? st.dx * 0.05 : dir === "left" ? -22 : 22;
    node.style.setProperty(dir === "right" ? "--s-like" : dir === "left" ? "--s-nope" : "--s-love", "1");
    armed.current = dir === "right" ? "like" : dir === "left" ? "nope" : "love";
    node.dataset.armed = armed.current;
    if (reduceMotion) {
      node.style.transition = "opacity 180ms ease";
      node.style.opacity = "0";
    } else {
      node.style.transition = "transform 300ms cubic-bezier(.3,.6,.4,1)";
      node.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`;
    }
    deckEl()?.style.setProperty("--progress", "1");
    window.setTimeout(() => onCommit(entry, dir), reduceMotion ? 170 : 260);
  }

  if (isTop) handleRef.current = { fling };

  // a card returning via undo flies back in from where it left
  useLayoutEffect(() => {
    const node = el.current;
    if (!node || !enterFrom) return;
    const w = window.innerWidth;
    const start =
      enterFrom === "left"
        ? `translate3d(${-w}px, 40px, 0) rotate(-20deg)`
        : enterFrom === "right"
          ? `translate3d(${w}px, 40px, 0) rotate(20deg)`
          : `translate3d(0, ${-window.innerHeight}px, 0)`;
    node.style.transition = "none";
    node.style.transform = reduceMotion ? "" : start;
    void node.offsetWidth;
    node.style.transition = "transform 420ms cubic-bezier(.2,1.1,.35,1)";
    node.style.transform = "";
  }, []);

  function down(ev: PointerEvent) {
    if (!isTop || ev.button > 0 || s.current.leaving) return;
    const node = el.current!;
    node.classList.remove("demo");
    node.setPointerCapture(ev.pointerId);
    node.style.transition = "none";
    deckEl()?.classList.add("dragging");
    Object.assign(s.current, {
      active: true,
      id: ev.pointerId,
      sx: ev.clientX,
      sy: ev.clientY,
      dx: 0,
      dy: 0,
      t0: performance.now(),
      lx: ev.clientX,
      ly: ev.clientY,
      lt: performance.now(),
      vx: 0,
      vy: 0,
    });
  }

  function move(ev: PointerEvent) {
    const st = s.current;
    if (!st.active || ev.pointerId !== st.id) return;
    const now = performance.now();
    const dt = Math.max(1, now - st.lt);
    st.vx = 0.7 * ((ev.clientX - st.lx) / dt) + 0.3 * st.vx;
    st.vy = 0.7 * ((ev.clientY - st.ly) / dt) + 0.3 * st.vy;
    st.lx = ev.clientX;
    st.ly = ev.clientY;
    st.lt = now;
    st.dx = ev.clientX - st.sx;
    st.dy = ev.clientY - st.sy;
    paint(st.dx, st.dy);
  }

  function up(ev: PointerEvent) {
    const st = s.current;
    if (!st.active || ev.pointerId !== st.id) return;
    st.active = false;
    const { dx, dy, vx, vy } = st;
    const dist = Math.hypot(dx, dy);
    const elapsed = performance.now() - st.t0;
    // a quick, still release is a tap
    if (dist < 8 && elapsed < 400) {
      deckEl()?.classList.remove("dragging");
      paint(0, 0);
      onTap(entry);
      return;
    }
    const upward = -dy > Math.abs(dx) * 1.1;
    if (upward && (dy < -120 || (vy < -0.55 && dy < -40))) return fling("up");
    if (!upward && (dx > 105 || (vx > 0.55 && dx > 30))) return fling("right");
    if (!upward && (dx < -105 || (vx < -0.55 && dx < -30))) return fling("left");
    // spring back
    const node = el.current!;
    deckEl()?.classList.remove("dragging");
    node.style.transition = "transform 480ms cubic-bezier(.18,1.4,.4,1)";
    node.style.transform = "";
    node.style.setProperty("--s-like", "0");
    node.style.setProperty("--s-nope", "0");
    node.style.setProperty("--s-love", "0");
    deckEl()?.style.setProperty("--progress", "0");
    arm("");
  }

  return (
    <div
      ref={el}
      class={`swipe-card depth-${Math.min(depth, 2)}${demo && isTop ? " demo" : ""}`}
      onAnimationEnd={(ev) => {
        if ((ev as AnimationEvent).animationName === "swipe-demo") (ev.currentTarget as HTMLElement).classList.remove("demo");
      }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      aria-hidden={isTop ? undefined : "true"}
      role={isTop ? "group" : undefined}
      aria-roledescription={isTop ? "name card" : undefined}
      aria-label={isTop ? entry.name : undefined}
      // a home for the keyboard shortcuts
      tabIndex={isTop ? 0 : undefined}
      aria-keyshortcuts={isTop ? "ArrowLeft ArrowRight ArrowUp I Z" : undefined}
    >
      {children}
      <div class="stamp stamp-like" aria-hidden="true">
        Like
      </div>
      <div class="stamp stamp-nope" aria-hidden="true">
        Nope
      </div>
      <div class="stamp stamp-love" aria-hidden="true">
        Love
      </div>
    </div>
  );
}

/** focused elements that use Enter and Space themselves */
const OWNS_ENTER = 'button, a[href], input, select, textarea, [contenteditable="true"], [role="radio"], [role="switch"], [role="dialog"]';
/** focused elements that use arrows and letters themselves: fields, and widgets moved with the arrows */
const OWNS_KEYS =
  'input, select, textarea, [contenteditable="true"], [role="radio"], [role="slider"], [role="tab"], [role="option"], [role="menuitem"], [role="dialog"]';

const NUDGE_KEY = "nameling:nudge-dismissed";
/** set once someone has loved a name or closed the tip about it */
const LOVE_TIP_KEY = "nameling:love-tip";

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  saveRaw(key, "1");
}

function EmptyDeck({ core }: { core: CoreData }) {
  const f = useStore(filters, (s) => s);
  const matching = useStore(deck, (s) => s.matching);
  const filtered = activeFilterCount(f) > 0;
  const none = filtered && matching === 0;
  return (
    <div class="deck-empty">
      <h2>{none ? "No names match these filters" : filtered ? "You've seen every name that fits" : "You've seen every name"}</h2>
      <p>
        {none
          ? "Try fewer or broader filters."
          : filtered
            ? "Loosen the filters to keep going, or look back over your shortlist."
            : `That's all ${fmt(core.entries.length)} names in the records. Time to pick a favourite.`}
      </p>
      <div class="row-gap">
        {filtered && (
          <button class="btn btn-primary" onClick={() => openOverlay("filters")}>
            Change filters
          </button>
        )}
        <button class="btn" onClick={() => goTab("list")}>
          Open shortlist
        </button>
      </div>
    </div>
  );
}

export function SwipeView({ core }: { core: CoreData }) {
  const rev = useStore(decisions, (s) => s.rev);
  const total = useStore(decisions, (s) => Object.keys(s.map).length);
  const deckState = useStore(deck, (s) => s);
  const st = useStore(settings, (s) => s);
  const f = useStore(filters, (s) => s);
  const paired = useStore(partner, (s) => !!s.code);
  const pSets = useStore(partnerSets, (s) => s);
  const tab = useStore(route, (r) => r.tab);
  const overlay = useStore(route, (r) => r.overlay);
  const matchOpen = useStore(ui, (s) => !!s.match);
  const handle = useRef<CardHandle | null>(null);
  const deckRef = useRef<HTMLDivElement>(null);
  const nopeRef = useRef<HTMLButtonElement>(null);
  const loveRef = useRef<HTMLButtonElement>(null);
  const likeRef = useRef<HTMLButtonElement>(null);
  const [enter, setEnter] = useState<{ key: string; dir: Dir } | null>(null);
  const [announce, setAnnounce] = useState("");
  const [nudgeDismissed, setNudgeDismissed] = useState(() => readFlag(NUDGE_KEY));
  const [knowsLove, setKnowsLove] = useState(() => readFlag(LOVE_TIP_KEY));
  // people who like names but never love one may not have found Love
  const loveTipDue = useStore(decisions, (s) => {
    let likes = 0;
    for (const k in s.map) {
      if (s.map[k].d === 2) return false;
      if (s.map[k].d === 1) likes++;
    }
    return likes >= 5;
  });
  const learnedLove = () => {
    setKnowsLove(true);
    setFlag(LOVE_TIP_KEY);
  };

  const cards = useMemo(() => upcoming(core, 3), [core, rev, deckState]);
  const because = useStore(tasteReasons, (s) => s.because);
  const remaining = useMemo(() => remainingCount(core), [core, rev, deckState]);
  const topKey = cards[0]?.key;

  useEffect(() => {
    prefetchDetails(upcoming(core, 6).map((e) => e.key));
  }, [topKey]);

  // once the next card has become the top card, settle the stack behind it
  useLayoutEffect(() => {
    deckRef.current?.style.setProperty("--progress", "0");
  }, [topKey]);

  function commit(e: NameEntry, dir: Dir) {
    const verdict = dir === "right" ? 1 : dir === "left" ? -1 : 2;
    const before = tally();
    refocus.current = !!document.activeElement?.classList.contains("swipe-card");
    decide(e.key, verdict, dir);
    tasteAfter(core, e.key, verdict);
    if (verdict === 2 && !knowsLove) learnedLove();
    setEnter(null);
    if (verdict !== 2) haptic(verdict === -1 ? 6 : 12);
    const next = upcoming(core, 2).find((x) => x.key !== e.key);
    const nsi = next ? sexIdxFor(next, st.sex) : 0;
    const said =
      `${verdict === -1 ? "Passed on" : verdict === 2 ? "Loved" : "Liked"} ${e.name}.` +
      (next ? ` Next: ${next.name}${next.r[nsi] ? `, #${next.r[nsi]} for ${nsi === 0 ? "girls" : "boys"}` : ""}.` : " That's the last name.");
    const matched = verdict > 0 && checkInstantMatch(e.key);
    if (matched) {
      haptic([30, 50, 30, 50, 60]);
      ui.set({ match: e.key });
    }
    // the effect starts where the card left the screen; a match has its own fanfare
    bump(verdict === 2 ? loveRef.current : verdict === 1 ? likeRef.current : nopeRef.current);
    const r = deckRef.current?.getBoundingClientRect();
    const exit = r && {
      x: verdict === 2 ? r.left + r.width / 2 : verdict === 1 ? r.right - 28 : r.left + 28,
      y: r.top + r.height * (verdict === 2 ? 0.3 : 0.45),
    };
    // a message the decision brought (a first like, a milestone) is read out with it, not over it
    const toastBefore = ui.get().toast?.id;
    cheer(verdict, exit, before, { quiet: matched });
    const t = ui.get().toast;
    if (t && t.id !== toastBefore) {
      ui.set({ toast: { ...t, spoken: true } });
      setAnnounce(`${said} ${t.text}`);
    } else setAnnounce(said);
  }

  function doUndo() {
    const step: UndoStep | null = undo();
    if (!step) return;
    bringToFront(step.key);
    setEnter({ key: step.key, dir: step.dir ?? "left" });
    const e = core.byKey.get(step.key);
    setAnnounce(e ? `Brought back ${e.name}` : "Undone");
    haptic(8);
  }

  // keyboard shortcuts while the deck is on screen (the hints under it list them), for keys the
  // focused control doesn't use itself: Enter and Space press a focused button, and arrows move
  // through a radio group or a slider
  useEffect(() => {
    if (tab !== "swipe" || overlay || matchOpen || !st.onboarded) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.defaultPrevented || ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const t = ev.target as HTMLElement | null;
      if (t?.closest?.(OWNS_KEYS)) return;
      if ((ev.key === "Enter" || ev.key === " ") && t?.closest?.(OWNS_ENTER)) return;
      const top = cards[0];
      if (ev.key === "ArrowRight" && top) handle.current?.fling("right");
      else if (ev.key === "ArrowLeft" && top) handle.current?.fling("left");
      else if (ev.key === "ArrowUp" && top) handle.current?.fling("up");
      else if (ev.key === "Backspace" || ev.key.toLowerCase() === "z") doUndo();
      else if ((ev.key === "Enter" || ev.key === " " || ev.key.toLowerCase() === "i") && top) openName(top.key);
      else return;
      ev.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, overlay, matchOpen, cards, st.onboarded]);

  // deciding from the focused card moves focus on to the next one, so the keyboard keeps its place
  const refocus = useRef(false);
  useLayoutEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    deckRef.current?.querySelector<HTMLElement>(".swipe-card.depth-0")?.focus({ preventScroll: true });
  }, [topKey]);

  const focus = f.collection === "partner" ? `${paired ? partnerName() : "Your partner"}'s picks` : deckFocus(f);
  const nFilters = activeFilterCount(f);
  const showLoveTip = loveTipDue && !knowsLove && cards.length > 0;
  const showNudge = !showLoveTip && !paired && !nudgeDismissed && total >= 5;
  // asked once, after the pair nudge has had its turn, never in the preview
  const askUsage = !__PREVIEW__ && !st.usageAsked && !showLoveTip && !showNudge && total >= 8;
  // the first card shows how swiping works, once the welcome screens are out of the way
  const showCoach = st.onboarded && total === 0 && cards.length > 0;
  const pname = paired ? partnerName() : "";
  const undoing = undoKey();
  const undoName = undoing ? core.byKey.get(undoing)?.name : undefined;

  return (
    <section class="swipe-view" aria-labelledby="swipe-title">
      <h1 class="sr-only" id="swipe-title">
        Swipe names
      </h1>
      <div class="deck-meta">
        {focus ? (
          <button class="meta-pill" onClick={() => filters.set({ collection: null, list: null })} aria-label={`Stop swiping ${focus}`}>
            {focus} <span aria-hidden="true">✕</span>
          </button>
        ) : (
          <span class="meta-text">
            {st.sex === "F" ? "Girls' names" : st.sex === "M" ? "Boys' names" : "Girls' & boys' names"}
            {nFilters ? ` · ${nFilters} filter${nFilters > 1 ? "s" : ""}` : ""}
          </span>
        )}
        <span class="meta-text">
          {fmt(remaining)} {remaining === 1 ? "name" : "names"} to go
        </span>
      </div>

      {showLoveTip && (
        <div class="nudge nudge-love">
          <IconStar size={20} filled />
          <p>
            Found a favourite? Swipe up or tap <strong>Love</strong>. Loved names go to the top of your shortlist, and
            still count as a yes for matches.
          </p>
          <button class="icon-btn" aria-label="Dismiss love tip" onClick={learnedLove}>
            <IconX size={18} />
          </button>
        </div>
      )}

      {showNudge && (
        <div class="nudge">
          <IconTwoHearts size={20} />
          <p>Choosing with someone? Pair up to see the names you both like.</p>
          <button class="btn btn-small btn-primary" onClick={() => goTab("together")}>
            Pair up
          </button>
          <button
            class="icon-btn"
            aria-label="Dismiss pairing tip"
            onClick={() => {
              setNudgeDismissed(true);
              setFlag(NUDGE_KEY);
            }}
          >
            <IconX size={18} />
          </button>
        </div>
      )}

      {askUsage && (
        <div class="nudge nudge-usage" role="group" aria-labelledby="usage-ask">
          <p id="usage-ask">
            Can Nameling count how it's used? Totals like names swiped and errors hit, with nothing that identifies you.
            You can change this in Settings.
          </p>
          <div class="row-gap">
            <button class="btn btn-small" onClick={() => settings.set({ shareUsage: true, usageAsked: true })}>
              Share counts
            </button>
            <button class="btn btn-small" onClick={() => settings.set({ shareUsage: false, usageAsked: true })}>
              No thanks
            </button>
          </div>
        </div>
      )}

      <div class="deck" ref={deckRef}>
        {cards.length === 0 ? (
          <EmptyDeck core={core} />
        ) : (
          cards
            .slice()
            .reverse()
            .map((e, i, arr) => {
              const depth = arr.length - 1 - i;
              const theirs = st.partnerHints && pSets.liked.has(e.key);
              const why = !theirs && because[e.key] ? entryFor(core, because[e.key]).name : null;
              const hint = theirs ? `${pname} likes this` : why ? `Because you liked ${why}` : null;
              return (
                <SwipeCard
                  key={e.key}
                  entry={e}
                  depth={depth}
                  handleRef={handle}
                  enterFrom={enter && enter.key === e.key ? enter.dir : null}
                  reduceMotion={st.reduceMotion}
                  demo={showCoach && depth === 0 && !st.reduceMotion}
                  onCommit={commit}
                  onTap={(x) => openName(x.key)}
                >
                  <NameCard
                    e={e}
                    pref={st.sex}
                    core={core}
                    hint={hint}
                    hintTaste={!!why}
                    also={otherSpellings(e.key, sexIdxFor(e, st.sex)).map((k) => entryFor(core, k).name)}
                  />
                </SwipeCard>
              );
            })
        )}

      </div>

      <div class="deck-actions" role="group" aria-label="Decide on this name">
        <button
          class="act act-undo"
          onClick={doUndo}
          disabled={!canUndo()}
          aria-label={undoName ? `Undo, bring back ${undoName}` : "Undo"}
          aria-keyshortcuts="z"
          title={__NATIVE__ ? undefined : "Undo (Z)"}
        >
          <span class="act-ico">
            <IconUndo size={22} />
          </span>
          <span class="act-label">Undo</span>
        </button>
        <button
          ref={nopeRef}
          class="act act-nope"
          onClick={() => handle.current?.fling("left")}
          disabled={!cards.length}
          aria-label={cards[0] ? `Nope, pass on ${cards[0].name}` : "Nope"}
          aria-keyshortcuts="ArrowLeft"
          title={__NATIVE__ ? undefined : "Nope (←)"}
        >
          <span class="act-ico">
            <IconX size={30} />
          </span>
          <span class="act-label">Nope</span>
        </button>
        <button
          ref={loveRef}
          class={`act act-love ${showLoveTip ? "pulse" : ""}`}
          onClick={() => handle.current?.fling("up")}
          disabled={!cards.length}
          aria-label={cards[0] ? `Love ${cards[0].name}, a favourite` : "Love"}
          aria-keyshortcuts="ArrowUp"
          title={__NATIVE__ ? undefined : "Love: a favourite (↑)"}
        >
          <span class="act-ico">
            <IconStar size={26} filled />
          </span>
          <span class="act-label">Love</span>
        </button>
        <button
          ref={likeRef}
          class="act act-like"
          onClick={() => handle.current?.fling("right")}
          disabled={!cards.length}
          aria-label={cards[0] ? `Like ${cards[0].name}` : "Like"}
          aria-keyshortcuts="ArrowRight"
          title={__NATIVE__ ? undefined : "Like (→)"}
        >
          <span class="act-ico">
            <IconHeart size={30} filled />
          </span>
          <span class="act-label">Like</span>
        </button>
        <button
          class="act act-info"
          onClick={() => cards[0] && openName(cards[0].key)}
          disabled={!cards.length}
          aria-label={cards[0] ? `Details about ${cards[0].name}` : "Details"}
          aria-keyshortcuts="i"
          title={__NATIVE__ ? undefined : "Details (I)"}
        >
          <span class="act-ico">
            <IconInfo size={22} />
          </span>
          <span class="act-label">Details</span>
        </button>
      </div>
      <p class="key-hints" aria-hidden="true">
        <kbd>←</kbd> nope <kbd>→</kbd> like <kbd>↑</kbd> love <kbd>Z</kbd> undo <kbd>I</kbd> details
      </p>
      <div class="sr-only" aria-live="polite">
        {announce}
      </div>
    </section>
  );
}

export function FilterButton() {
  const n = useStore(filters, (f) => activeFilterCount(f));
  return (
    <button class="icon-btn filter-btn" onClick={() => openOverlay("filters")} aria-label={`Filters${n ? `, ${n} active` : ""}`}>
      <IconSliders size={22} />
      {n > 0 && <span class="badge">{n}</span>}
    </button>
  );
}
